import * as React from 'react';
import { Panel } from '../../../components/containers/Panel';
import { useForm } from 'react-hook-form';
import { FormOptions } from '../../../constants/form';
import { Toggle } from '../../../components/forms/Toggle';
import { Select } from '../../../components/forms/Select';
import { PasswordModal } from '../PasswordModal';
import { NameEmailModal } from '../NameEmailModal';
import {
  DateFormatSetting,
  TimeFormatSetting,
  UserOrganizationRole,
  StartOfWeekFormatSetting,
  TimezoneSetting
} from '../../../models/graphql';
import { ToggleAction } from '../../../components/forms/Toggle/Toggle.controller';
import { CenteredContentLayout } from '../../../components/containers/CenteredContentLayout';
import { PanelHeader } from '../../../components/containers/Panel/PanelHeader.view';
import { ContainerListItem } from '../../../components/actions/ListItem/ListItem.view';
import { SettingsType } from '../../../hooks/useSettings';
import { ButtonMenu } from '../../../components/actions/ButtonMenu';
import { FormElementAction } from '../../../models/basic-objects';
import { mapTimezone } from '../../../helpers/map-timezone';
import { useState } from 'react';
import { ViewPersonalDataModal } from '../../Policy/ViewPersonalDataModal';
import { useUserData } from '../../../hooks/useUserData';
import { Debugger } from '../../../components/containers/Debugger';

export type AccountViewProps = {
  name: string;
  email: string;
  openPasswordModal: boolean;
  setOpenPasswordModal: (openPasswordModal: boolean) => void;
  openNameEmailModal: boolean;
  setOpenNameEmailModal: (openNameModal: boolean) => void;
  openDotMenu: boolean;
  setOpenDotMenu: (openDotMenu: boolean) => void;
  selectedOrgRole: UserOrganizationRole | null;
  settings: SettingsType;
  timezoneSelectAction: FormElementAction<string>;
  timeFormatSelectAction: FormElementAction<TimeFormatSetting>;
  startOfWeekSelectAction: FormElementAction<StartOfWeekFormatSetting>;
  dateFormatSelectAction: FormElementAction<DateFormatSetting>;
  productAnalyticsToggleAction: ToggleAction;
  onlineMarketingToggleAction: ToggleAction;
  selectedTimezone: TimezoneSetting;
};

export const AccountView = (props: AccountViewProps): JSX.Element => {
  const [showPersonalDataModal, setShowPersonalDataModal] = useState(false);

  const toggleRules = {};
  const dateRules = {};
  const timeRules = {};
  const { userData } = useUserData();
  const isPasswordSet = !!userData?.passwordSet;
  const useFormInstance = useForm({
    ...FormOptions
  });

  const timeZoneListItems = mapTimezone();

  const squareButtonMenuListItems: ContainerListItem[] = [
    {
      action: () => props.setOpenNameEmailModal(true),
      label: 'Edit Name & Email'
    }
  ];

  const dotMenu = (
    <ButtonMenu isSquareButton={true} buttonType={'transparent'} side={'right'} listItems={squareButtonMenuListItems} />
  );

  return (
    <CenteredContentLayout headerTitle={'My Account'} loading={!props.settings} hasSidebar>
      <Panel>
        <PanelHeader title={props.name} subTitle={props.email} squareButtonMenu={dotMenu} />
        <Debugger tooltipContent={'User ID: ' + userData?.id} modalContent={userData} />
        <div className={'cell small-12 margin-bottom-small-32'}>
          <h6 className={'uppercase'}>
            <a onClick={() => props.setOpenPasswordModal(true)}>
              {isPasswordSet ? 'Change Password' : 'Set a password'}
            </a>
          </h6>
        </div>
      </Panel>
      <Panel>
        <PanelHeader title={'Day & Time Format'} />
        <div className={'grid-x grid-margin-x'}>
          <div className={'cell small-12 tablet-portrait-6 margin-bottom-small-16'}>
            <Select
              items={[
                {
                  label: 'MM/DD/YY',
                  value: DateFormatSetting.MONTH_DAY_YEAR
                },
                {
                  label: 'DD/MM/YY',
                  value: DateFormatSetting.DAY_MONTH_YEAR
                }
              ]}
              selectedValue={props.settings.selectedOrgSettings?.dateFormat}
              label={'Date Format'}
              name={'dateFormats'}
              size={'large'}
              action={props.dateFormatSelectAction as FormElementAction}
              rules={dateRules}
              useFormInstance={useFormInstance}
            />
          </div>
          <div className={'cell small-12 tablet-portrait-6 margin-bottom-small-16'}>
            <Select
              items={[
                {
                  label: 'Sunday',
                  value: StartOfWeekFormatSetting.SUNDAY
                },
                {
                  label: 'Monday',
                  value: StartOfWeekFormatSetting.MONDAY
                }
              ]}
              selectedValue={props.settings.selectedOrgSettings?.startOfWeekFormat}
              label={'Week Starts From'}
              name={'startOfWeek'}
              size={'large'}
              action={props.startOfWeekSelectAction as FormElementAction}
              rules={dateRules}
              useFormInstance={useFormInstance}
            />
          </div>
          <div className={'cell small-12 tablet-portrait-6 margin-bottom-small-16'}>
            <Select
              items={[
                {
                  label: '12h (7:45 PM)',
                  value: TimeFormatSetting.HOUR_12
                },
                {
                  label: '24h (19:45)',
                  value: TimeFormatSetting.HOUR_24
                }
              ]}
              selectedValue={props.settings.selectedOrgSettings?.timeFormat}
              label={'Time Format'}
              name={'timeFormats'}
              size={'large'}
              action={props.timeFormatSelectAction as FormElementAction}
              rules={timeRules}
              useFormInstance={useFormInstance}
            />
          </div>
          <div className={'cell small-12 tablet-portrait-6 margin-bottom-small-16'}>
            <Select
              items={timeZoneListItems}
              selectedValue={props.selectedTimezone}
              label={'Timezone'}
              name={'timezone'}
              size={'large'}
              rules={{}}
              action={props.timezoneSelectAction as FormElementAction}
              useFormInstance={useFormInstance}
            />
          </div>
        </div>
      </Panel>
      <Panel>
        <PanelHeader title={'Privacy Preferences'} />
        {props.selectedOrgRole === UserOrganizationRole.OWNER && (
          <div className={'grid-x align-justify margin-bottom-small-24'}>
            <Toggle
              id={`analytics-${props.settings.selectedOrgSettings?.id}`}
              formInstance={useFormInstance}
              rules={toggleRules}
              name={'analytics'}
              label={'Customer Support & Product Analytics'}
              helper={`We use analytics tools to help us make improvements to SmarterQueue, and provide you with bespoke Customer Support. If you turn this off, it will be harder for us to help you if you encounter any issues.`}
              action={props.productAnalyticsToggleAction}
              checked={props.settings.selectedOrgSettings?.productAnalyticsEnabled || false}
            />
          </div>
        )}
        <div className={'grid-x align-justify margin-bottom-small-32'}>
          <Toggle
            rules={toggleRules}
            formInstance={useFormInstance}
            name={'marketing'}
            label={'Online Marketing'}
            helper={
              "We may wish to show you special offers and product updates via Facebook/Google ads in the future. If you disable this, we will not enable Facebook/Google's tracking code."
            }
            action={props.onlineMarketingToggleAction}
            checked={props.settings.userSettings?.onlineMarketing || false}
          />
        </div>
      </Panel>
      <Panel>
        <PanelHeader title={'Personal Data'} />
        <div className={'cell shrink margin-bottom-small-32'}>
          <a onClick={() => setShowPersonalDataModal(true)}>View personal data</a> we have stored on our servers.
        </div>
      </Panel>
      {props.openPasswordModal && <PasswordModal onClose={() => props.setOpenPasswordModal(false)} />}
      {props.openNameEmailModal && <NameEmailModal onClose={() => props.setOpenNameEmailModal(false)} />}
      {showPersonalDataModal && <ViewPersonalDataModal closeModal={() => setShowPersonalDataModal(false)} />}
    </CenteredContentLayout>
  );
};
